import { Input } from "@admin/components/atoms/Input";
import useClickOutside from "@admin/hooks/useClickOutside";
import { LocationUser } from "@enos-monorepo/lib";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Info } from "./Info";

interface PersonnelGroup {
  title: string;
  options: LocationUser[];
}

interface SelectPersonnelsWithGroupProps {
  options: PersonnelGroup[];
  onSelectPersonnel: (personnel: LocationUser) => void;
  selectedPersonnel: LocationUser | null;
}

export function SelectPersonnelsWithGroup({
  options,
  onSelectPersonnel,
  selectedPersonnel,
}: SelectPersonnelsWithGroupProps) {
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const ref = useRef(null);

  const { t } = useTranslation();

  useClickOutside(ref, () => setShow(false));

  const onClickPersonnel = (personnel: LocationUser) => {
    onSelectPersonnel(personnel);
    setShow(false);
  };

  const filteredOptions = options.map((group) => ({
    ...group,
    options: group.options.filter((personnel) =>
      `${personnel.firstName} ${personnel.lastName} ${personnel.userName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    ),
  }));

  return (
    <div ref={ref} className="relative w-full cursor-pointer">
      <div
        onClick={() => setShow((prev) => !prev)}
        className="border-[1px] border-[#DBDFE9] rounded-md text-sm flex flex-row justify-between items-center"
      >
        {selectedPersonnel ? (
          <Info
            name={`${selectedPersonnel?.firstName} ${selectedPersonnel?.lastName}`}
            userName={selectedPersonnel?.userName}
            userPhoto={selectedPersonnel?.userPhoto}
          >
            {show ? (
              <i className="ki-filled ki-up"></i>
            ) : (
              <i className="ki-filled ki-down"></i>
            )}
          </Info>
        ) : (
          <p className="text-gray-400 px-4 py-2">Select Personnel</p>
        )}
      </div>

      {show && (
        <div className="absolute dropdown-content left-0 flex flex-col z-40 bg-white w-full rounded-b-md gap-5">
          <div className="px-4 pt-5">
            <Input
              type="text"
              size="sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("common.Search Personnel")}
              className="px-4 py-2 border-b border-[#DBDFE9]"
            />
          </div>

          <div className="h-[300px] overflow-auto">
            {filteredOptions.map((group) => (
              <div key={group.title}>
                <div className="px-4 py-2.5 text-sm leading-[14px] text-[#252F4A] font-medium">
                  {group.title}
                </div>
                {group.options.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400">
                    {t("shift.Personnel Not Found")}
                  </div>
                ) : (
                  group.options.map((personnel) => (
                    <div
                      key={personnel.userId}
                      className={`relative ${
                        selectedPersonnel?.userId === personnel.userId
                          ? "bg-[#EFF6FF] text-white"
                          : ""
                      }`}
                    >
                      <Info
                        onClick={() => onClickPersonnel(personnel)}
                        name={personnel.firstName + " " + personnel.lastName}
                        userName={personnel.userName}
                        userPhoto={personnel.userPhoto}
                      />
                      {selectedPersonnel?.userId === personnel.userId && (
                        <i className="ki-filled ki-check absolute right-[5%] top-[50%] translate-y-[-50%] text-[#1B84FF] "></i>
                      )}
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
